import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePageContentMutations } from './use-page-content-hooks';
import { useBatchAttachPageResearch } from '@/hooks/universal-creation-research';
import { PageGenerationRequest } from '@/types/page-generation-types';

interface PageGenerationTask {
  id: 'structure' | 'content' | 'research' | 'saving';
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
}

interface PageGenerationState {
  status: 'idle' | 'processing' | 'success' | 'error';
  progress: number;
  tasks: PageGenerationTask[];
  currentTaskDescription: string;
  error: string | null;
  generatedPageId: string | null;
}

const initialState: PageGenerationState = {
  status: 'idle',
  progress: 0,
  tasks: [
    { id: 'structure', name: 'Building Page Structure', status: 'pending' },
    { id: 'content', name: 'Generating Content', status: 'pending' },
    { id: 'research', name: 'Attaching Research', status: 'pending' },
    { id: 'saving', name: 'Saving Page', status: 'pending' },
  ],
  currentTaskDescription: '',
  error: null,
  generatedPageId: null,
};

export const usePageGeneration = (workbookId: string) => {
  const [state, setState] = useState<PageGenerationState>(initialState);
  const queryClient = useQueryClient();
  const { createPageAsync } = usePageContentMutations(workbookId);
  const batchAttachResearchMutation = useBatchAttachPageResearch();

  const updateTaskStatus = useCallback((taskId: string, status: 'pending' | 'in-progress' | 'completed' | 'failed') => {
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === taskId ? { ...task, status } : task
      )
    }));
  }, []);

  const startGeneration = useCallback(async (
    request: PageGenerationRequest,
    selectedResearch: any[],
    summaryData: Record<string, string>
  ) => {
    setState(prev => ({ ...prev, status: 'processing', error: null }));

    try {
      // Step 1: Building Page Structure
      setState(prev => ({ ...prev, progress: 10, currentTaskDescription: 'Analyzing your requirements and building page structure...' }));
      updateTaskStatus('structure', 'in-progress');
      
      // Small delay to show the structure step
      await new Promise(resolve => setTimeout(resolve, 800));
      updateTaskStatus('structure', 'completed');

      // Step 2: Generate AI Content with dedicated thread
      setState(prev => ({ ...prev, progress: 25, currentTaskDescription: 'Generating intelligent content based on your inputs...' }));
      updateTaskStatus('content', 'in-progress');

      // Prepare research summaries for page creation
      const pageResearchSummaries = selectedResearch.map(research => ({
        title: research.research_library.title,
        summary: summaryData[research.id] || research.research_library.content?.substring(0, 200) || ''
      }));

      // Prepare clarifying questions
      const clarifyingQuestions = request.clarifyingQuestions || [];

      console.log('[PageGeneration] Creating page with dedicated thread:', {
        pageResearch: pageResearchSummaries.length,
        clarifyingQuestions: clarifyingQuestions.length,
        threadId: request.goalThreadId, // This is now a dedicated page thread
        workbookId: request.workbook_id
      });

      // Create the page with AI content using dedicated thread
      const newPage = await createPageAsync({
        workbook_id: request.workbook_id, // Can be null for private pages
        title: request.title,
        initial_request: request.initial_request,
        type: request.type,
        writing_style: request.writing_style,
        goalThreadId: request.goalThreadId, // ✅ UNIVERSAL: Dedicated thread for this specific page
        researchSummaries: pageResearchSummaries,
        clarifyingQuestions: clarifyingQuestions
      });

      updateTaskStatus('content', 'completed');
      setState(prev => ({ ...prev, progress: 70, currentTaskDescription: 'Content generated successfully! Attaching research context...' }));

      // Step 3: Attach Research Context
      updateTaskStatus('research', 'in-progress');
      
      if (selectedResearch.length > 0) {
        const researchAttachments = selectedResearch.map(research => ({
          research,
          summary: summaryData[research.id] || ''
        }));

        await batchAttachResearchMutation.mutateAsync({
          entityId: newPage.id,
          researchAttachments
        });
      }

      updateTaskStatus('research', 'completed');
      setState(prev => ({ ...prev, progress: 90, currentTaskDescription: 'Finalizing and saving your page...' }));

      // Step 4: Saving (already done, but show for completion)
      updateTaskStatus('saving', 'in-progress');
      
      // Brief delay to show saving step
      await new Promise(resolve => setTimeout(resolve, 500));
      
      updateTaskStatus('saving', 'completed');

      // Invalidate relevant queries based on page type
      if (request.workbook_id) {
        // Project page
        queryClient.invalidateQueries({ queryKey: ['workbook-page-content', 'workbook', request.workbook_id] });
        queryClient.invalidateQueries({ queryKey: ['project-navigation', request.workbook_id] });
      } else {
        // Private page
        queryClient.invalidateQueries({ queryKey: ['private-pages'] });
      }

      setState(prev => ({ 
        ...prev, 
        status: 'success',
        progress: 100,
        currentTaskDescription: 'Page created successfully!',
        generatedPageId: newPage.id
      }));

    } catch (error: any) {
      console.error('[PageGeneration] Error:', error);
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Failed to create page. Please try again.',
        currentTaskDescription: 'An error occurred during page creation.'
      }));
    }
  }, [createPageAsync, batchAttachResearchMutation, updateTaskStatus, queryClient]);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    state,
    startGeneration,
    reset
  };
}; 